/**
 * Property Responses Section
 * 
 * A lightweight wrapper for displaying form responses within a Property Drawer.
 * Reuses the existing form responses infrastructure but with a compact layout
 * suited for embedded use cases.
 */

import React from 'react';
import { useFormResponses, useFormResponseDetail } from './hooks/useFormResponses';
import { DesktopFormResponseDetailDrawer, MobileFormResponseDetailSheet } from './detail';
import { formatResponseForTable } from './utils/formResponseFormatters';
import { useAuth } from '../../context/AuthContext';

/**
 * Desktop version of property responses
 */
export const DesktopPropertyResponsesSection = ({ propertyId, propertyName }) => {
    const { user, loading: authLoading } = useAuth();
    const ownerId = user?.uid;

    // Fetch responses for this property
    const { responses, loading, error } = useFormResponses(ownerId, {
        propertyId,
        type: 'property',
    });

    // Detail view management
    const detail = useFormResponseDetail();

    // Create a map of raw responses by ID for detail view
    const rawResponseMap = React.useMemo(() => {
        return responses.reduce((map, response) => {
            map[response.id] = response;
            return map;
        }, {});
    }, [responses]);

    // Handle row click
    const handleRowClick = (response) => {
        const rawResponse = rawResponseMap[response.id];
        detail.open(rawResponse || response);
    };

    // Loading state
    if (authLoading || loading) {
        return (
            <div className="text-center py-8 text-stone-400 text-xs">
                Loading responses...
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="text-center py-8">
                <div className="text-red-500 text-xs">Error loading responses</div>
                <p className="text-[10px] text-stone-400 mt-1">{error}</p>
            </div>
        );
    }

    // Empty state
    if (responses.length === 0) {
        return (
            <div className="text-center py-8">
                <div className="text-stone-400 text-xs">No responses yet</div>
                <p className="text-[10px] text-stone-300 mt-1">Form submissions will appear here</p>
            </div>
        );
    }

    // Format responses for display
    const formatContext = propertyId && propertyName
        ? { propertiesMap: { [propertyId]: propertyName } }
        : {};
    const formattedResponses = responses.map(r => formatResponseForTable(r, formatContext));

    return (
        <div className="space-y-4">
            <table className="w-full text-left">
                <thead className="bg-stone-50 border-b border-notion-border">
                    <tr>
                        <th className="py-2 px-3 text-[10px] font-semibold text-stone-400 uppercase">Name</th>
                        <th className="py-2 px-3 text-[10px] font-semibold text-stone-400 uppercase">Email</th>
                        <th className="py-2 px-3 text-[10px] font-semibold text-stone-400 uppercase text-right">Time</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-notion-border">
                    {formattedResponses.map((response) => (
                        <tr
                            key={response.id}
                            className="hover:bg-stone-50 cursor-pointer"
                            onClick={() => handleRowClick(response)}
                        >
                            <td className="p-3 text-xs font-medium text-stone-700">{response.name || 'Unknown'}</td>
                            <td className="p-3 text-xs text-stone-500">{response.email || '-'}</td>
                            <td className="p-3 text-right text-[10px] text-stone-400">{response.submittedAtFormatted}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Detail Drawer */}
            <DesktopFormResponseDetailDrawer
                response={detail.response}
                isOpen={detail.isOpen}
                onClose={detail.close}
            />
        </div>
    );
};

/**
 * Mobile version of property responses
 */
export const MobilePropertyResponsesSection = ({ propertyId, propertyName }) => {
    const { user, loading: authLoading } = useAuth();
    const ownerId = user?.uid;

    // Fetch responses for this property
    const { responses, loading, error } = useFormResponses(ownerId, {
        propertyId,
        type: 'property',
    });

    // Detail view management
    const detail = useFormResponseDetail();

    // Create a map of raw responses by ID for detail view
    const rawResponseMap = React.useMemo(() => {
        return responses.reduce((map, response) => {
            map[response.id] = response;
            return map;
        }, {});
    }, [responses]);

    // Handle card click
    const handleCardClick = (response) => {
        const rawResponse = rawResponseMap[response.id];
        detail.open(rawResponse || response);
    };

    // Loading state
    if (authLoading || loading) {
        return (
            <div className="text-center py-8 text-stone-400 text-xs">
                Loading responses...
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="bg-white border border-notion-border rounded-xl p-4 text-center">
                <div className="text-red-500 text-xs">Error loading responses</div>
                <p className="text-[10px] text-stone-400 mt-1">{error}</p>
            </div>
        );
    }

    // Empty state
    if (responses.length === 0) {
        return (
            <div className="bg-white border border-notion-border rounded-xl p-4 text-center">
                <div className="text-stone-400 text-xs">No responses yet</div>
                <p className="text-[10px] text-stone-300 mt-1">Form submissions will appear here</p>
            </div>
        );
    }

    // Format responses for display
    const mobileFormatContext = propertyId && propertyName
        ? { propertiesMap: { [propertyId]: propertyName } }
        : {};
    const formattedResponses = responses.map(r => formatResponseForTable(r, mobileFormatContext));

    return (
        <div className="space-y-3">
            {formattedResponses.map((response) => (
                <div
                    key={response.id}
                    className="bg-white border border-notion-border rounded-xl p-3 shadow-sm cursor-pointer active:bg-stone-50 transition-colors"
                    onClick={() => handleCardClick(response)}
                >
                    <div className="flex justify-between items-start mb-1">
                        <h3 className="text-sm font-semibold text-notion-text">{response.name || 'Unknown'}</h3>
                        <span className="text-[10px] text-stone-400">{response.submittedAtFormatted}</span>
                    </div>
                    <p className="text-xs text-stone-500 mb-2">{response.email || '-'}</p>
                </div>
            ))}

            {/* Detail Sheet */}
            <MobileFormResponseDetailSheet
                response={detail.response}
                isOpen={detail.isOpen}
                onClose={detail.close}
            />
        </div>
    );
};

export default {
    Desktop: DesktopPropertyResponsesSection,
    Mobile: MobilePropertyResponsesSection,
};
